import type { AllergenCode, MenuCategory, MenuItem, MenuVariant } from './types';
import type { DayHours, HoursException } from './hours';

/**
 * The editable content files under content/, and how they become the data the
 * site renders.
 *
 * Editors (through the CMS at /admin) write these files; scripts/build-content.mjs
 * runs them through the functions here at build and writes the generated data
 * under lib/data/ (menu.json, hours-exceptions.json, site-copy.json). Every problem is reported at once with the file it is in,
 * and nothing is written when there are any, so a bad edit fails the build and
 * the live site stays as it was.
 *
 * Only relative imports and no runtime dependencies: this runs in Node under
 * type stripping for the build script as well as in the app and its tests.
 */

// ---- shapes of the content files ------------------------------------------

/** content/menu/categories/<slug>.json */
export interface ContentCategory {
  name: string;
  order?: number;
}

/** content/menu/items/<slug>.json. Prices in pounds, as an editor types them. */
export interface ContentItem {
  name: string;
  category: string;
  description?: string;
  price: number;
  allergens?: string[];
  vegetarian?: boolean;
  vegan?: boolean;
  variants?: { label: string; price: number }[];
  photo?: { image?: string; alt?: string; caption?: string };
  available?: boolean;
  order?: number;
}

/** content/hours/exceptions.json */
export interface ContentHoursException {
  when: 'date' | 'range' | 'recurring';
  date?: string;
  from?: string;
  to?: string;
  recurring?: string;
  closed: boolean;
  open?: string;
  close?: string;
  note?: string;
}

/** content/site.json */
export interface ContentSite {
  headlines: { line1: string; line2: string }[];
  heroParagraph: string;
  story: { label: string; heading: string; paragraph: string };
  collectionTime: string;
  specials?: {
    label?: string;
    days?: Partial<Record<DayKey, { name?: string; description?: string; price?: number }>>;
  };
  photos: Record<PhotoKey, { image: string; alt: string; caption?: string; note?: string }>;
  reviews?: { quote?: string; name?: string; when?: string; source?: string }[];
}

/** The photograph slots the site has. Each is one editor field. */
export const PHOTO_KEYS = ['grill', 'flatbread', 'heroLeft', 'heroMiddle', 'heroRight'] as const;
export type PhotoKey = (typeof PHOTO_KEYS)[number];
/** The prints beside the headline, which carry a handwritten caption. */
export const HERO_PHOTO_KEYS = [
  'heroLeft',
  'heroMiddle',
  'heroRight',
] as const satisfies readonly PhotoKey[];
export const CAPTION_MAX = 32;
export const NOTE_MAX = 160;

/** content/shop.json: the facts about the shop that the owners may change. */
export interface ContentShop {
  /** Off until opening day: the site says the shop is opening and hides the ways to order. */
  trading: boolean;
  /** YYYY-MM-DD. With trading off, the site turns itself on when this day arrives in London. */
  openingDate?: string;
  phone: string;
  email: string;
  address: { street: string; town: string; postcode: string };
  hours: Record<DayKey, { closed: boolean; open?: string; close?: string }>;
  delivery: {
    enabled: boolean;
    timeAim?: string;
    bands?: { upToMiles: number; fee: number; minimumOrder: number }[];
    terms?: string[];
  };
  halal: { certified: boolean; certifier?: string };
  parkingNote?: string;
  menuProvisional: boolean;
  fsaEstablishmentId?: string;
  /** Required by law in the footer only if the business is a limited company. */
  company?: { name?: string; number?: string; registeredOffice?: string; vatNumber?: string };
}

export const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;
export type DayKey = (typeof DAY_KEYS)[number];

/** What the site reads: content/shop.json with the links and pence derived. */
export interface ShopDetails {
  trading: boolean;
  openingDate: string | null;
  phone: string;
  phoneHref: string;
  whatsappHref: string;
  email: string;
  street: string;
  town: string;
  postcode: string;
  /** Sunday first, matching the hours engine. */
  weekHours: (DayHours | null)[];
  delivery: {
    enabled: boolean;
    /** The outermost band, or null when delivery is off. */
    radiusMiles: number | null;
    /** The lowest minimum, or null when delivery is off. */
    minimumOrderPence: number | null;
    timeAim: string | null;
    bands: { upToMiles: number; feePence: number; minimumOrderPence: number }[];
    terms: string[];
  };
  halal: { certified: boolean; certifier: string | null };
  parkingNote: string | null;
  menuProvisional: boolean;
  fsaEstablishmentId: string | null;
  company: {
    name: string;
    number: string;
    registeredOffice: string;
    vatNumber: string | null;
  } | null;
}

/** What the site reads for its copy: content/site.json plus measured photos. */
export const REVIEW_MAX = 240;

export interface SiteCopy extends Omit<ContentSite, 'photos' | 'specials' | 'reviews'> {
  /** Real customer quotes, typed in by the owners. Empty means nothing shows. */
  reviews: { quote: string; name: string; when: string | null; source: string | null }[];
  /** The daily special, or null when no day has one. */
  specials: {
    label: string;
    days: Record<DayKey, { name: string; description: string | null; pricePence: number } | null>;
  } | null;
  photos: Record<
    PhotoKey,
    {
      image: string;
      alt: string;
      width: number;
      height: number;
      /** A few words written on the print by hand. Hero prints only. */
      caption?: string;
      /** A sentence shown when the print is opened. Hero prints only. */
      note?: string;
    }
  >;
}

/** lib/data/menu.json: what the site and the printable menu render. */
export interface MenuDocument {
  provisional: boolean;
  categories: MenuCategory[];
}

export interface Result<T> {
  value: T | null;
  errors: string[];
}

// ---- helpers ---------------------------------------------------------------

/** File names and ids: what Sveltia's slug settings produce. Exported so every check agrees. */
export const SLUG = /^[a-z0-9][a-z0-9-]*$/;
/** Shapes shared with the editor's form validation (admin/cms-config.ts). */
export const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
export const YMD = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export const MD = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
/** Photo files as the editor uploads them (slugified names) and the image pipeline converts them. */
export const PHOTO_PATH = /^\/img\/[a-z0-9][a-z0-9-]*\.(jpe?g|png)$/;
/** Photos narrower than this ship blurry on a laptop, since variants are never upscaled. */
export const PHOTO_MIN_WIDTH = 1200;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True for a YYYY-MM-DD that exists on the calendar (no 31 February). */
export function isRealDate(iso: string): boolean {
  if (!YMD.test(iso)) return false;
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** True for an MM-DD that exists in some year (29 February is allowed). */
export function isRealMonthDay(md: string): boolean {
  return MD.test(md) && isRealDate(`2024-${md}`);
}

/**
 * Sessions closing between 01:00 and 01:59 fall inside the hour the clocks
 * repeat or skip in March and October, which the wall-clock hours engine cannot
 * resolve. Nobody needs it, so refuse it plainly.
 */
export function isSupportedClose(close: string): boolean {
  return !/^01:/.test(close);
}
const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i;
/** Longest headline line that stays on one line at the hero's 16ch measure. */
export const HEADLINE_MAX = 22;

/**
 * A UK phone number as an owner types it ("07508 693304", "+44 7508 693304")
 * to the display form (as typed, spaces tidied), the tel: link and the
 * WhatsApp link (country code, digits only). Null if it is not a phone number.
 */
export function normalisePhone(
  raw: unknown,
): { display: string; tel: string; whatsapp: string } | null {
  if (typeof raw !== 'string') return null;
  const display = raw.trim().replace(/\s+/g, ' ');
  const digits = display.replace(/[^\d+]/g, '');
  if (!/^\+?\d{10,15}$/.test(digits)) return null;
  let international = digits;
  if (digits.startsWith('+')) international = digits.slice(1);
  else if (digits.startsWith('0')) international = `44${digits.slice(1)}`;
  const tel = digits.startsWith('+') ? digits : `+${international}`;
  return { display, tel: `tel:${tel}`, whatsapp: `https://wa.me/${international}` };
}

/** 8.5 pounds is 850 pence; more than two decimals is a mistake, not a price. */
export function poundsToPence(pounds: unknown): number | null {
  if (typeof pounds !== 'number' || !Number.isFinite(pounds) || pounds < 0) return null;
  const pence = Math.round(pounds * 100);
  if (Math.abs(pounds * 100 - pence) > 1e-6) return null;
  return pence;
}

function byOrderThenName<T extends { order?: number; name: string }>(a: T, b: T): number {
  const ao = a.order ?? Number.MAX_SAFE_INTEGER;
  const bo = b.order ?? Number.MAX_SAFE_INTEGER;
  return ao - bo || a.name.localeCompare(b.name, 'en-GB');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ---- menu ------------------------------------------------------------------

/**
 * Categories and items, each keyed by the slug of the file it came from, to
 * the menu the site renders. Categories order by `order` then name; items the
 * same within their category. Items marked unavailable are left out.
 */
/** The no-dashes rule, for copy typed in the editor. */
function noDashesIn(fail: (message: string) => void, field: string, value: unknown) {
  if (typeof value === 'string' && /[\u2013\u2014]/.test(value)) {
    fail(`${field} contains an em or en dash; use a comma, a full stop or a colon`);
  }
}

type Measure = (image: string) => { width: number; height: number } | null;

/**
 * One photograph slot: path under /img/, a serveable format with a simple
 * file name, present in assets/img, wide enough. Returns the measured photo
 * or null after reporting the problem.
 */
function checkPhoto(
  fail: (message: string) => void,
  field: string,
  photo: { image?: string; alt?: string } | undefined,
  measure: Measure,
): { image: string; alt: string; width: number; height: number } | null {
  if (!photo?.image?.trim() || !photo?.alt?.trim()) {
    fail(`${field} needs image and alt`);
    return null;
  }
  if (!photo.image.startsWith('/img/')) {
    fail(`${field}.image must be under /img/ (got "${photo.image}")`);
    return null;
  }
  if (!PHOTO_PATH.test(photo.image)) {
    fail(
      `${field}.image must be a JPEG or PNG with a simple lowercase file name (got "${photo.image}")`,
    );
    return null;
  }
  const size = measure(photo.image);
  if (!size) {
    fail(`${field}.image "${photo.image}" is not in assets/img`);
    return null;
  }
  if (size.width < PHOTO_MIN_WIDTH) {
    fail(`${field}.image is ${size.width}px wide; it needs to be at least ${PHOTO_MIN_WIDTH}px`);
    return null;
  }
  return { image: photo.image, alt: photo.alt.trim(), ...size };
}

export function contentToMenu(
  categories: Record<string, unknown>,
  items: Record<string, unknown>,
  allergenCodes: readonly AllergenCode[],
  options: { provisional?: boolean; measure?: Measure } = {},
): Result<MenuDocument> {
  // Without a measurer (tests, tooling) photos cannot be checked and are dropped.
  const measure = options.measure;
  const errors: string[] = [];
  const codes = new Set<string>(allergenCodes);

  const cats: (ContentCategory & { id: string })[] = [];
  for (const [slug, raw] of Object.entries(categories)) {
    const where = `menu/categories/${slug}.json`;
    if (!SLUG.test(slug)) errors.push(`${where}: file name must be a lowercase slug`);
    if (!isRecord(raw) || typeof raw.name !== 'string' || !raw.name.trim()) {
      errors.push(`${where}: needs a name`);
      continue;
    }
    if (raw.order !== undefined && typeof raw.order !== 'number') {
      errors.push(`${where}: order must be a number`);
    }
    cats.push({ id: slug, name: raw.name.trim(), order: raw.order as number | undefined });
  }
  cats.sort(byOrderThenName);
  const menu: MenuCategory[] = cats.map((c) => ({ id: c.id, name: c.name, items: [] }));
  const byId = new Map(menu.map((c) => [c.id, c]));

  const parsed: { item: MenuItem; name: string; category: string; order?: number }[] = [];
  for (const [slug, raw] of Object.entries(items)) {
    const where = `menu/items/${slug}.json`;
    const fail = (message: string) => errors.push(`${where}: ${message}`);
    if (!SLUG.test(slug)) fail('file name must be a lowercase slug');
    if (!isRecord(raw)) {
      fail('not an object');
      continue;
    }
    const item = raw as Partial<ContentItem>;
    if (item.available === false) continue;
    if (typeof item.name !== 'string' || !item.name.trim()) fail('needs a name');
    if (typeof item.category !== 'string' || !byId.has(item.category)) {
      fail(`category "${String(item.category ?? '')}" does not exist`);
    }
    const pricePence = poundsToPence(item.price);
    if (pricePence === null) fail(`price must be a number of pounds with at most two decimals`);

    const allergens: AllergenCode[] = [];
    if (item.allergens !== undefined && !Array.isArray(item.allergens))
      fail('allergens must be a list');
    for (const code of Array.isArray(item.allergens) ? item.allergens : []) {
      if (!codes.has(code)) fail(`unknown allergen code "${code}"`);
      else if (!allergens.includes(code as AllergenCode)) allergens.push(code as AllergenCode);
    }

    const variants: MenuVariant[] = [];
    if (item.variants !== undefined && !Array.isArray(item.variants))
      fail('variants must be a list');
    for (const variant of Array.isArray(item.variants) ? item.variants : []) {
      const vp = poundsToPence(variant?.price);
      if (!variant?.label?.trim() || vp === null) {
        fail('each variant needs a label and a price in pounds');
        continue;
      }
      variants.push({ label: variant.label.trim(), pricePence: vp });
    }
    if (item.order !== undefined && typeof item.order !== 'number') fail('order must be a number');

    let photo: MenuItem['photo'];
    // An empty photo object is what the editor saves when the field is left
    // alone, so only a photo with something in it is checked.
    if (measure && item.photo && (item.photo.image || item.photo.alt || item.photo.caption)) {
      const checked = checkPhoto(fail, 'photo', item.photo, measure);
      const caption = item.photo.caption?.trim();
      if (caption && caption.length > CAPTION_MAX) {
        fail(
          `photo.caption is ${caption.length} characters; the print has room for ${CAPTION_MAX}`,
        );
      } else if (checked) {
        noDashesIn(fail, 'photo.caption', caption);
        photo = caption ? { ...checked, caption } : checked;
      }
    }

    if (typeof item.name !== 'string' || pricePence === null || typeof item.category !== 'string')
      continue;
    const out: MenuItem = { id: slug, name: item.name.trim(), pricePence, allergens };
    if (photo) out.photo = photo;
    if (typeof item.description === 'string' && item.description.trim()) {
      out.description = item.description.trim();
    }
    if (item.vegetarian || item.vegan) out.vegetarian = true;
    if (item.vegan) out.vegan = true;
    if (variants.length) out.variants = variants;
    parsed.push({ item: out, name: out.name, category: item.category, order: item.order });
  }

  parsed.sort(byOrderThenName);
  for (const { item, category } of parsed) byId.get(category)?.items.push(item);

  const populated = menu.filter((c) => c.items.length > 0);
  if (!errors.length && populated.length === 0) errors.push('menu: no available items');
  if (errors.length) return { value: null, errors };
  return {
    value: { provisional: options.provisional ?? false, categories: populated },
    errors: [],
  };
}

// ---- hours exceptions ------------------------------------------------------

export function contentToHoursExceptions(raw: unknown): Result<HoursException[]> {
  const errors: string[] = [];
  const where = 'hours/exceptions.json';
  if (!isRecord(raw)) return { value: null, errors: [`${where}: not an object`] };
  // The editor omits an emptied optional list entirely, so a missing key means none.
  const list = raw.exceptions ?? [];
  if (!Array.isArray(list))
    return { value: null, errors: [`${where}: "exceptions" must be a list`] };
  const out: HoursException[] = [];

  // Bank holidays ticked in the editor become one exception each, under one
  // rule (closed, or the given hours). They go first so that a hand-written
  // exception for the same date wins.
  const ticked = raw.bankHolidays ?? [];
  if (!Array.isArray(ticked)) {
    errors.push(`${where}: "bankHolidays" must be a list of dates`);
  } else if (ticked.length > 0) {
    const rule = isRecord(raw.bankHolidayHours)
      ? (raw.bankHolidayHours as Partial<ContentHoursException>)
      : {};
    let hours: DayHours | null = null;
    let ok = true;
    if (rule.closed === false) {
      if (!HHMM.test(rule.open ?? '') || !HHMM.test(rule.close ?? '')) {
        errors.push(`${where}: bank holiday hours need open and close times as HH:MM, or closed`);
        ok = false;
      } else if (!isSupportedClose(rule.close!)) {
        errors.push(`${where}: bank holiday closing between 01:00 and 01:59 is not supported`);
        ok = false;
      } else hours = { open: rule.open!, close: rule.close! };
    }
    const note =
      typeof rule.note === 'string' && rule.note.trim() ? rule.note.trim() : 'Bank holiday';
    for (const date of ticked) {
      if (typeof date !== 'string' || !isRealDate(date)) {
        errors.push(`${where}: bank holiday "${String(date)}" is not a real date as YYYY-MM-DD`);
        continue;
      }
      if (ok) out.push({ date, hours, note });
    }
  }

  list.forEach((entry: unknown, index: number) => {
    const at = `${where}, exception ${index + 1}`;
    const fail = (message: string) => errors.push(`${at}: ${message}`);
    if (!isRecord(entry)) {
      fail('not an object');
      return;
    }
    const e = entry as Partial<ContentHoursException>;
    let hours: DayHours | null = null;
    if (e.closed !== true) {
      if (!HHMM.test(e.open ?? '') || !HHMM.test(e.close ?? '')) {
        fail('needs open and close times as HH:MM, or closed set to true');
        return;
      }
      if (!isSupportedClose(e.close!)) {
        return fail('closing between 01:00 and 01:59 is not supported; use 01:00 or 02:00');
      }
      hours = { open: e.open!, close: e.close! };
    }
    const note = typeof e.note === 'string' && e.note.trim() ? { note: e.note.trim() } : {};
    if (e.when === 'date') {
      if (!isRealDate(e.date ?? '')) return fail('date must be a real date as YYYY-MM-DD');
      out.push({ date: e.date!, hours, ...note });
    } else if (e.when === 'range') {
      if (!isRealDate(e.from ?? '') || !isRealDate(e.to ?? '')) {
        return fail('from and to must be real dates as YYYY-MM-DD');
      }
      if (e.from! > e.to!) return fail('from must not be after to');
      out.push({ from: e.from!, to: e.to!, hours, ...note });
    } else if (e.when === 'recurring') {
      if (!isRealMonthDay(e.recurring ?? '')) return fail('recurring must be a real day as MM-DD');
      out.push({ recurring: e.recurring!, hours, ...note });
    } else {
      fail('when must be date, range or recurring');
    }
  });
  if (errors.length) return { value: null, errors };
  return { value: out, errors: [] };
}

// ---- site copy -------------------------------------------------------------

/**
 * `measure` returns a photo's pixel size, or null if the file is missing; the
 * build supplies it (sharp), tests supply a stub.
 */
export function contentToSiteCopy(raw: unknown, measure: Measure): Result<SiteCopy> {
  const errors: string[] = [];
  const where = 'site.json';
  const fail = (message: string) => errors.push(`${where}: ${message}`);
  // The no-dashes rule is test-enforced in code; this is where it reaches copy
  // typed in the editor, which deploys without passing those tests.
  const noDashes = (field: string, value: unknown) => noDashesIn(fail, field, value);
  if (!isRecord(raw)) return { value: null, errors: [`${where}: not an object`] };
  const site = raw as Partial<ContentSite>;

  const headlines = Array.isArray(site.headlines) ? site.headlines : [];
  if (!headlines.length) fail('needs at least one headline');
  const seen = new Set<string>();
  headlines.forEach((h, i) => {
    if (!h?.line1?.trim() || !h?.line2?.trim()) return fail(`headline ${i + 1} needs both lines`);
    for (const [label, line] of [
      ['first', h.line1],
      ['second', h.line2],
    ] as const) {
      if (line.trim().length > HEADLINE_MAX) {
        fail(
          `headline ${i + 1}: the ${label} line is over ${HEADLINE_MAX} characters and would wrap to a third line`,
        );
      }
    }
    const key = `${h.line1.trim()} ${h.line2.trim()}`.toLowerCase();
    if (seen.has(key)) fail(`headline ${i + 1} repeats an earlier one`);
    seen.add(key);
  });
  if (!site.heroParagraph?.trim()) fail('needs heroParagraph');
  if (
    !site.story?.label?.trim() ||
    !site.story?.heading?.trim() ||
    !site.story?.paragraph?.trim()
  ) {
    fail('story needs label, heading and paragraph');
  }
  if (!site.collectionTime?.trim()) fail('needs collectionTime');

  (site.headlines ?? []).forEach((h, i) => {
    noDashes(`headline ${i + 1}`, h?.line1);
    noDashes(`headline ${i + 1}`, h?.line2);
  });
  noDashes('heroParagraph', site.heroParagraph);
  noDashes('story.heading', site.story?.heading);
  noDashes('story.paragraph', site.story?.paragraph);
  noDashes('collectionTime', site.collectionTime);
  // The daily special: a label and up to seven days, each with a name and a
  // price. A day left blank is simply not shown.
  let specials: SiteCopy['specials'] = null;
  const rawSpecials = site.specials;
  if (rawSpecials !== undefined) {
    if (!isRecord(rawSpecials)) fail('specials must be an object');
    else {
      const days = {} as NonNullable<SiteCopy['specials']>['days'];
      let any = false;
      for (const day of DAY_KEYS) {
        const entry = rawSpecials.days?.[day];
        const name = typeof entry?.name === 'string' ? entry.name.trim() : '';
        const description = typeof entry?.description === 'string' ? entry.description.trim() : '';
        if (!name && !description && entry?.price === undefined) {
          days[day] = null;
          continue;
        }
        const pricePence = poundsToPence(entry?.price);
        if (!name) {
          fail(`specials.days.${day} needs a name`);
          days[day] = null;
          continue;
        }
        if (pricePence === null) {
          fail(`specials.days.${day}.price must be a number of pounds`);
          days[day] = null;
          continue;
        }
        noDashes(`specials.days.${day}.name`, name);
        noDashes(`specials.days.${day}.description`, description);
        days[day] = { name, description: description || null, pricePence };
        any = true;
      }
      const label = typeof rawSpecials.label === 'string' ? rawSpecials.label.trim() : '';
      noDashes('specials.label', label);
      if (any && !label) fail('specials.label is needed when any day has a special');
      if (any && label) specials = { label, days };
    }
  }

  const reviews: SiteCopy['reviews'] = [];
  if (site.reviews !== undefined && !Array.isArray(site.reviews)) fail('reviews must be a list');
  (Array.isArray(site.reviews) ? site.reviews : []).forEach((review, i) => {
    const at = `reviews[${i + 1}]`;
    const quote = review?.quote?.trim() ?? '';
    const name = review?.name?.trim() ?? '';
    if (!quote || !name) {
      fail(`${at} needs a quote and a name`);
      return;
    }
    if (quote.length > REVIEW_MAX) {
      fail(`${at}.quote is ${quote.length} characters; keep it under ${REVIEW_MAX}`);
      return;
    }
    noDashes(`${at}.quote`, quote);
    reviews.push({
      quote,
      name,
      when: review?.when?.trim() || null,
      source: review?.source?.trim() || null,
    });
  });

  for (const key of PHOTO_KEYS) {
    noDashes(`photos.${key}.caption`, site.photos?.[key]?.caption);
    noDashes(`photos.${key}.note`, site.photos?.[key]?.note);
  }

  const photos: Partial<SiteCopy['photos']> = {};
  for (const key of PHOTO_KEYS) {
    const photo = site.photos?.[key];
    const checked = checkPhoto(fail, `photos.${key}`, photo, measure);
    if (!checked || !photo) continue;
    const caption = photo.caption?.trim();
    const note = photo.note?.trim();
    if (caption && caption.length > CAPTION_MAX) {
      fail(
        `photos.${key}.caption is ${caption.length} characters; the print has room for ${CAPTION_MAX}`,
      );
      continue;
    }
    if (note && note.length > NOTE_MAX) {
      fail(`photos.${key}.note is ${note.length} characters; keep it under ${NOTE_MAX}`);
      continue;
    }
    photos[key] = {
      ...checked,
      ...(caption ? { caption } : {}),
      ...(note ? { note } : {}),
    };
  }

  if (errors.length) return { value: null, errors };
  return {
    value: {
      headlines: headlines.map((h) => ({ line1: h.line1.trim(), line2: h.line2.trim() })),
      heroParagraph: site.heroParagraph!.trim(),
      story: {
        label: site.story!.label.trim(),
        heading: site.story!.heading.trim(),
        paragraph: site.story!.paragraph.trim(),
      },
      collectionTime: site.collectionTime!.trim(),
      specials,
      reviews,
      photos: photos as SiteCopy['photos'],
    },
    errors: [],
  };
}

// ---- JSON on disk ----------------------------------------------------------

/**
 * Checks lib/data/menu.json (written by scripts/build-content.mjs) has the
 * shape the site expects. Throws with every problem at once, so a bad file
 * fails the build rather than rendering nonsense.
 */
export function menuFromJson(data: unknown, allergenCodes: readonly AllergenCode[]): MenuDocument {
  const errors: string[] = [];
  const codes = new Set<string>(allergenCodes);
  const doc = data as Partial<MenuDocument> | null;

  if (!doc || typeof doc !== 'object') throw new Error('menu.json: not an object');
  if (typeof doc.provisional !== 'boolean') errors.push('"provisional" must be true or false');
  if (!Array.isArray(doc.categories) || doc.categories.length === 0) {
    throw new Error('menu.json: "categories" must be a non-empty array');
  }

  const categoryIds = new Set<string>();
  const itemIds = new Set<string>();
  doc.categories.forEach((category, ci) => {
    const where = `category ${ci + 1}`;
    if (!category?.id || !SLUG.test(category.id)) errors.push(`${where}: bad id`);
    if (!category?.name) errors.push(`${where}: missing name`);
    if (categoryIds.has(category?.id)) errors.push(`${where}: duplicate id "${category.id}"`);
    categoryIds.add(category?.id);
    if (!Array.isArray(category?.items) || category.items.length === 0) {
      errors.push(`${where}: no items`);
      return;
    }
    category.items.forEach((item, ii) => {
      const at = `${where}, item ${ii + 1}`;
      if (!item?.id || !SLUG.test(item.id)) errors.push(`${at}: bad id`);
      if (itemIds.has(item?.id)) errors.push(`${at}: duplicate id "${item.id}"`);
      itemIds.add(item?.id);
      if (!item?.name) errors.push(`${at}: missing name`);
      if (!Number.isInteger(item?.pricePence) || item.pricePence < 0) {
        errors.push(`${at}: pricePence must be a non-negative integer`);
      }
      if (!Array.isArray(item?.allergens)) errors.push(`${at}: allergens must be a list`);
      else {
        for (const code of item.allergens) {
          if (!codes.has(code)) errors.push(`${at}: unknown allergen code "${code}"`);
        }
      }
      if (item?.vegan && !item.vegetarian) errors.push(`${at}: vegan implies vegetarian`);
      if (item?.photo !== undefined) {
        const p = item.photo;
        if (!p?.image || !p?.alt || !Number.isInteger(p?.width) || !Number.isInteger(p?.height)) {
          errors.push(`${at}: photo needs image, alt, width and height`);
        }
      }
      for (const variant of item?.variants ?? []) {
        if (!variant?.label || !Number.isInteger(variant.pricePence)) {
          errors.push(`${at}: each variant needs a label and integer pricePence`);
        }
      }
    });
  });

  if (errors.length) throw new Error(`menu.json:\n  ${errors.join('\n  ')}`);
  return { provisional: doc.provisional as boolean, categories: doc.categories as MenuCategory[] };
}

// ---- shop details ----------------------------------------------------------

export function contentToShopDetails(raw: unknown): Result<ShopDetails> {
  const errors: string[] = [];
  const where = 'shop.json';
  const fail = (message: string) => errors.push(`${where}: ${message}`);
  if (!isRecord(raw)) return { value: null, errors: [`${where}: not an object`] };
  const shop = raw as Partial<ContentShop>;
  if (typeof shop.trading !== 'boolean') fail('trading must be true or false');
  const openingDate = typeof shop.openingDate === 'string' ? shop.openingDate.trim() : '';
  if (openingDate && !isRealDate(openingDate))
    fail('openingDate must be a real date as YYYY-MM-DD');

  const phone = normalisePhone(shop.phone);
  if (!phone) fail('phone must be a UK phone number, e.g. 01252 000000 or +44 1252 000000');

  const email = typeof shop.email === 'string' ? shop.email.trim() : '';
  if (!EMAIL.test(email)) fail('email does not look like an email address');

  const address = shop.address;
  const street = address?.street?.trim() ?? '';
  const town = address?.town?.trim() ?? '';
  const postcode = address?.postcode?.trim().toUpperCase() ?? '';
  if (!street || !town) fail('address needs street and town');
  if (!UK_POSTCODE.test(postcode)) fail(`postcode "${postcode}" does not look like a UK postcode`);

  const weekHours: (DayHours | null)[] = [];
  for (const day of DAY_KEYS) {
    const entry = shop.hours?.[day];
    if (!isRecord(entry)) {
      fail(`hours.${day} is missing`);
      weekHours.push(null);
      continue;
    }
    if (entry.closed === true) {
      weekHours.push(null);
      continue;
    }
    if (!HHMM.test(String(entry.open ?? '')) || !HHMM.test(String(entry.close ?? ''))) {
      fail(`hours.${day} needs open and close times as HH:MM, or closed set to true`);
      weekHours.push(null);
      continue;
    }
    if (!isSupportedClose(String(entry.close))) {
      fail(`hours.${day}: closing between 01:00 and 01:59 is not supported; use 01:00 or 02:00`);
      weekHours.push(null);
      continue;
    }
    weekHours.push({ open: String(entry.open), close: String(entry.close) });
  }
  if (!errors.some((e) => e.includes('hours.')) && weekHours.every((d) => d === null)) {
    fail('the shop cannot be closed every day of the week');
  }

  // Delivery: a switch, bands by distance, an optional time aim, and the terms.
  // The bands and terms are validated even while delivery is off, so the day it
  // is switched on nothing new can break.
  const rawDelivery = shop.delivery ?? ({} as Partial<ContentShop['delivery']>);
  if (typeof rawDelivery.enabled !== 'boolean') fail('delivery.enabled must be true or false');
  const bands: ShopDetails['delivery']['bands'] = [];
  if (rawDelivery.bands !== undefined && !Array.isArray(rawDelivery.bands)) {
    fail('delivery.bands must be a list');
  }
  for (const [i, band] of (Array.isArray(rawDelivery.bands) ? rawDelivery.bands : []).entries()) {
    const at = `delivery.bands[${i + 1}]`;
    const upToMiles = band?.upToMiles;
    const feePence = poundsToPence(band?.fee);
    const minimumOrderPence = poundsToPence(band?.minimumOrder);
    if (typeof upToMiles !== 'number' || !(upToMiles > 0)) {
      fail(`${at}.upToMiles must be a positive number of miles`);
      continue;
    }
    if (feePence === null) {
      fail(`${at}.fee must be a number of pounds (0 for free)`);
      continue;
    }
    if (minimumOrderPence === null) {
      fail(`${at}.minimumOrder must be a number of pounds`);
      continue;
    }
    const last = bands.at(-1);
    if (last && upToMiles <= last.upToMiles) {
      fail(`${at}.upToMiles must be further than the band before it`);
      continue;
    }
    bands.push({ upToMiles, feePence, minimumOrderPence });
  }
  if (rawDelivery.enabled === true && bands.length === 0) {
    fail('delivery is switched on but has no distance bands');
  }
  const timeAim = typeof rawDelivery.timeAim === 'string' ? rawDelivery.timeAim.trim() : '';
  noDashesIn(fail, 'delivery.timeAim', timeAim);
  const terms: string[] = [];
  if (rawDelivery.terms !== undefined && !Array.isArray(rawDelivery.terms)) {
    fail('delivery.terms must be a list of sentences');
  }
  for (const term of Array.isArray(rawDelivery.terms) ? rawDelivery.terms : []) {
    if (typeof term !== 'string' || !term.trim()) continue;
    noDashesIn(fail, 'delivery.terms', term);
    terms.push(term.trim());
  }

  if (typeof shop.halal?.certified !== 'boolean') fail('halal.certified must be true or false');
  if (typeof shop.menuProvisional !== 'boolean') fail('menuProvisional must be true or false');
  const fsa = typeof shop.fsaEstablishmentId === 'string' ? shop.fsaEstablishmentId.trim() : '';
  if (fsa && !/^\d+$/.test(fsa)) fail('fsaEstablishmentId must be the number from the FSA listing');

  // A limited company must show its name, number and registered office; a
  // sole trader leaves all of this blank and nothing is shown.
  const co = shop.company ?? {};
  const coName = co.name?.trim() ?? '';
  const coNumber = co.number?.trim() ?? '';
  const coOffice = co.registeredOffice?.trim() ?? '';
  const vat = co.vatNumber?.trim() ?? '';
  let company: ShopDetails['company'] = null;
  if (coName || coNumber || coOffice || vat) {
    if (!coName || !coNumber || !coOffice) {
      fail(
        'company needs name, number and registeredOffice together, or all blank for a sole trader',
      );
    } else if (!/^[A-Z0-9]{8}$/i.test(coNumber)) {
      fail('company.number should be the 8-character Companies House number');
    } else {
      company = {
        name: coName,
        number: coNumber.toUpperCase(),
        registeredOffice: coOffice,
        vatNumber: vat || null,
      };
    }
  }

  if (errors.length) return { value: null, errors };
  return {
    value: {
      trading: shop.trading === true,
      openingDate: openingDate || null,
      phone: phone!.display,
      phoneHref: phone!.tel,
      whatsappHref: phone!.whatsapp,
      email,
      street,
      town,
      postcode,
      weekHours,
      delivery: {
        enabled: rawDelivery.enabled === true,
        radiusMiles: rawDelivery.enabled === true ? (bands.at(-1)?.upToMiles ?? null) : null,
        minimumOrderPence:
          rawDelivery.enabled === true
            ? Math.min(...bands.map((band) => band.minimumOrderPence))
            : null,
        timeAim: timeAim || null,
        bands,
        terms,
      },
      halal: {
        certified: shop.halal!.certified,
        certifier: shop.halal?.certifier?.trim() ? shop.halal.certifier.trim() : null,
      },
      parkingNote: shop.parkingNote?.trim() ? shop.parkingNote.trim() : null,
      menuProvisional: shop.menuProvisional as boolean,
      fsaEstablishmentId: fsa || null,
      company,
    },
    errors: [],
  };
}
